import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Hero } from "@/components/Hero";
import { CTASection } from "@/components/CTASection";
import { SectionCard } from "@/components/SectionCard";
import { CompletedProjectsSlideshow } from "@/components/CompletedProjectsSlideshow";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import heroVideo from "@/assets/video-prvi-2.webm.asset.json";

import { products } from "@/data/products";

// Kitchen images
import leonardoKitchen from "@/assets/kitchens/leonardo-01.jpg";
import adriaticoKitchen from "@/assets/kitchens/adriatico-01.jpg";
import abuDhabiKitchen from "@/assets/kitchens/abu-dhabi-01.jpg";
import maximusKitchen from "@/assets/kitchens/maximus-01.jpg";
import orbitaKitchen from "@/assets/kitchens/orbita-01.jpg";
import pureKitchen from "@/assets/kitchens/pure-01.jpg";
import royaleKitchen from "@/assets/kitchens/royale-01.jpg";
import imperialKitchen from "@/assets/kitchens/imperial-1.webp.asset.json";

// All kitchens data
const allKitchens = [
  { name: "Leonardo", slug: "leonardo", image: leonardoKitchen },
  { name: "Adriatico", slug: "adriatico", image: adriaticoKitchen },
  { name: "Abu Dhabi", slug: "abu-dhabi", image: abuDhabiKitchen },
  { name: "Maximus", slug: "maximus", image: maximusKitchen },
  { name: "Orbita", slug: "orbita", image: orbitaKitchen },
  { name: "Pure", slug: "pure", image: pureKitchen },
  { name: "Royale", slug: "royale", image: royaleKitchen },
  { name: "Imperial", slug: "imperial", image: imperialKitchen.url },
];

// Project images
import apartmentUmag from "@/assets/projects/apartment-umag-01.jpg";
import vetClinic from "@/assets/projects/vet-clinic-01.jpg";
import kitchenS from "@/assets/projects/kitchen-s-01.webp";
import kitchenKT from "@/assets/projects/kuhinja-kt-02.webp";
import interierL from "@/assets/projects/interier-l-01.png";
import interierUS from "@/assets/projects/interier-us-01.webp";

// All projects data
const allProjects = [
  {
    title: "Interier US",
    description: "Japandi stil, hrast, Dekton kamen",
    image: interierUS,
    slug: "interier-us",
    category: "Interier",
  },
  {
    title: "Interier L",
    description: "Japandi stil, hrast, bež RAL 9001",
    image: interierL,
    slug: "interier-l",
    category: "Interier",
  },
  {
    title: "Kuhinja KT",
    description: "Moderna kuhinja z belimi frontami",
    image: kitchenKT,
    slug: "kuhinja-kt",
    category: "Kuhinja",
  },
  {
    title: "Kuhinja S",
    description: "Alpi furnir in tehnični kamen",
    image: kitchenS,
    slug: "kuhinja-s",
    category: "Kuhinja",
  },
  {
    title: "Stanovanje U",
    description: "Celovita prenova stanovanja",
    image: apartmentUmag,
    slug: "stanovanje-u",
    category: "Interier",
  },
  {
    title: "Veterinarska ambulanta",
    description: "Projektiranje in oprema po meri",
    image: vetClinic,
    slug: "veterinarska-ambulanta",
    category: "Poslovni prostor",
  },
];

// Sample apartments
import emonikaHero from "@/assets/projects/emonika-hero.webp";
import soseskaNovaHero from "@/assets/projects/soseska-nova-hero.webp";
import nordikaHero from "@/assets/projects/nordika-hero.jpg";
import jurckovoNaseljeHero from "@/assets/projects/jurckovo-naselje-hero.webp";

const Index = () => {
  // Get 3 random products on every reload
  const randomProducts = useMemo(() => {
    const shuffled = [...products].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  }, []);

  // Get 2 random kitchens on every reload
  const randomKitchens = useMemo(() => {
    const shuffled = [...allKitchens].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2);
  }, []);

  // Get 2 random projects on every reload
  const randomProjects = useMemo(() => {
    const shuffled = [...allProjects].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2);
  }, []);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Interoform – Kuhinje in mizarstvo po meri</title>
        <meta name="description" content="Interoform ustvarja ekskluzivno pohištvo in kuhinje po meri iz vrhunskih materialov. Mizarstvo za arhitekte in celovite rešitve za vaš dom." />
        <link rel="canonical" href="https://interoform.com" />
      
        <meta property="og:title" content="Interoform – Kuhinje in mizarstvo po meri" />
        <meta property="og:description" content="Interoform ustvarja ekskluzivno pohištvo in kuhinje po meri iz vrhunskih materialov. Mizarstvo za arhitekte in celovite rešitve za vaš dom." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://interoform.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Interoform – Kuhinje in mizarstvo po meri" />
        <meta name="twitter:description" content="Interoform ustvarja ekskluzivno pohištvo in kuhinje po meri iz vrhunskih materialov. Mizarstvo za arhitekte in celovite rešitve za vaš dom." />
      </Helmet>
      <Navbar />
      
      <main>
        {/* Hero Section - Background Video */}
        <section className="relative w-full h-[75vh] min-h-[600px] overflow-hidden">
          {/* Background Video */}
          <div className="absolute inset-0 animate-ken-burns">
            <video
              src={heroVideo.url}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              aria-label="Moderna kuhinja po meri"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />
          </div>

          {/* Content */}
          <div className="relative h-full container flex flex-col justify-end pb-16 md:pb-24">
            <div className="max-w-4xl animate-fade-in">
              {/* Elegant eyebrow */}
              <p className="text-white/70 text-sm uppercase tracking-[0.4em] mb-6 font-light">
                Arhitekturni biro & Mizarstvo
              </p>
              
              {/* Main headline - Classical serif typography */}
              <h1 className="mb-6 font-serif">
                <span className="block text-white text-4xl md:text-5xl lg:text-7xl font-normal tracking-tight leading-[1.1]">
                  Oblikujemo prostore,
                </span>
                <span className="block text-white/90 text-4xl md:text-5xl lg:text-7xl font-normal tracking-tight leading-[1.1] italic">
                  ki navdihujejo
                </span>
              </h1>
              
              {/* Subtitle with gold accent bar */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-gold to-gold/50" />
                <p className="text-white/80 text-lg md:text-xl font-light tracking-wide">
                  Pohištvo po meri • Kuhinje • Interierji
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="text-base px-8 py-6 bg-white text-foreground hover:bg-white/90 shadow-xl">
                  <Link to="/kontakt">
                    Brezplačen posvet
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" className="text-base px-8 py-6 bg-transparent border-2 border-white text-white hover:bg-white hover:text-foreground backdrop-blur-sm">
                  <Link to="/projekti">
                    Oglej si projekte
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Tagline Section - Classical Design */}
        <section className="py-24 md:py-40 bg-ivory relative overflow-hidden">
          {/* Decorative gold accent line */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent via-gold to-transparent" />
          
          <div className="container">
            <div className="text-center max-w-4xl mx-auto">
              {/* Elegant eyebrow text */}
              <p className="text-sm uppercase tracking-[0.3em] text-gold-dark font-medium mb-8">
                Od ideje do izvedbe
              </p>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal leading-tight mb-6 text-charcoal">
                Natančno projektiranje,<br />
                <span className="font-medium text-foreground">dovršena izdelava</span>,<br />
                <span className="italic text-bronze">interier po vaši meri.</span>
              </h2>
              
              <div className="flex items-center justify-center gap-8 mt-12">
                <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-muted" />
                <p className="text-lg text-muted-foreground italic font-serif">
                  Od arhitekturnega načrta do mizarskega detajla
                </p>
                <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-muted" />
              </div>
            </div>
          </div>
          
          {/* Bottom decorative accent */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-t from-transparent via-gold to-transparent" />
        </section>

      {/* Completed projects */}
      <CompletedProjectsSlideshow />

      {/* Kitchen Showcases - 2 Random Kitchens */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container">
          {/* Section header */}
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-bronze font-medium mb-2">Kolekcije</p>
              <h2 className="text-3xl md:text-4xl">Kuhinje po meri</h2>
            </div>
            <Button asChild variant="ghost" className="hidden md:flex">
              <Link to="/kuhinje">
                Vse kuhinje po meri
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {randomKitchens.map((kitchen) => (
            <Link key={kitchen.slug} to={`/kuhinje/${kitchen.slug}`} className="group block">
              <div className="relative h-[500px] w-full overflow-hidden bg-muted md:h-[600px]">
                <img
                  src={kitchen.image}
                  alt={`Kuhinja ${kitchen.name}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                <div className="relative flex h-full items-end">
                  <div className="container pb-8 md:pb-12">
                    <p className="text-white/70 text-sm uppercase tracking-wider mb-2">Kolekcija</p>
                    <h3 className="text-white text-3xl md:text-4xl font-semibold mb-4">{kitchen.name}</h3>
                    <Button
                      variant="secondary"
                      size="lg"
                      className="bg-white text-foreground shadow-lg hover:bg-white/90"
                    >
                      Oglej si več
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="container mt-8 text-center md:hidden">
          <Button asChild variant="outline">
            <Link to="/kuhinje">
              Vse kuhinje po meri
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-bronze font-medium mb-2">Katalog</p>
              <h2 className="text-3xl md:text-4xl">Produkti</h2>
            </div>
            <Button asChild variant="ghost" className="hidden md:flex">
              <Link to="/produkti">
                Vsi produkti
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {randomProducts.map((product) => (
              <Link key={product.id} to={`/produkti/${product.slug}`} className="group">
                <div className="relative overflow-hidden rounded-xl bg-card shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                    <span className="text-bronze font-medium inline-flex items-center group-hover:gap-2 transition-all">
                      Oglej si več
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="mt-8 text-center md:hidden">
            <Button asChild variant="outline">
              <Link to="/produkti">
                Vsi produkti
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>


      {/* Projects Section */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-bronze font-medium mb-2">Portfolio</p>
              <h2 className="text-3xl md:text-4xl">Izbrani projekti</h2>
            </div>
            <Button asChild variant="ghost" className="hidden md:flex">
              <Link to="/projekti">
                Vsi projekti
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {randomProjects.map((project) => (
              <Link key={project.slug} to={`/projekti/${project.slug}`} className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <div className="relative h-[400px] md:h-[450px]">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <p className="text-white/60 text-sm uppercase tracking-wider mb-2">{project.category}</p>
                      <h3 className="text-white text-2xl md:text-3xl font-semibold mb-2">{project.title}</h3>
                      <p className="text-white/80 mb-4">{project.description}</p>
                      <span className="inline-flex items-center text-accent font-medium group-hover:gap-2 transition-all">
                        Oglej si projekt
                        <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="mt-8 text-center md:hidden">
            <Button asChild variant="outline">
              <Link to="/projekti">
                Vsi projekti
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Sample Apartments Section */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-bronze font-medium mb-2">Projektiranje</p>
              <h2 className="text-3xl md:text-4xl">Vzorčna stanovanja</h2>
              <p className="text-lg text-muted-foreground mt-4 max-w-xl">
                Referenčni projekti arhitekturnih načrtov za nova stanovanja
              </p>
            </div>
            <Button asChild variant="ghost" className="hidden md:flex">
              <Link to="/projektiranje">
                Vsi načrti
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <SectionCard
              title="Vzorčno stanovanje Emonika"
              description="Ekskluzivno stanovanje v novi mestni vstopni točki Ljubljane."
              image={emonikaHero}
              imageAlt="Vzorčno stanovanje Emonika Ljubljana"
              href="/projektiranje/emonika"
              buttonText="Odpri načrte"
            />

            <SectionCard
              title="Vzorčno stanovanje Soseska Nova Koper"
              description="Optimalno načrtovanje za sodobno družinsko stanovanje ob morju."
              image={soseskaNovaHero}
              imageAlt="Vzorčno stanovanje Soseska Nova Koper"
              href="/projektiranje/soseska-nova"
              buttonText="Odpri načrte"
            />

            <SectionCard
              title="Vzorčno stanovanje Nordika"
              description="Funkcionalna zasnova za življenje v središču mesta."
              image={nordikaHero}
              imageAlt="Vzorčno stanovanje Nordika Ljubljana"
              href="/projektiranje/nordika"
              buttonText="Odpri načrte"
            />

            <SectionCard
              title="Vzorčno stanovanje Jurčkovo naselje"
              description="Biofilna zasnova za sodobno bivanje na Rudniku v Ljubljani."
              image={jurckovoNaseljeHero}
              imageAlt="Vzorčno stanovanje Jurčkovo naselje Ljubljana Rudnik"
              href="/projektiranje/jurckovo-naselje"
              buttonText="Odpri načrte"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Začnimo vaš projekt"
        description="Kontaktirajte nas za brezplačen posvet in skupaj ustvarimo interier vaših sanj."
        buttonText="Kontaktirajte nas"
        buttonHref="/kontakt"
        variant="accent"
      />
      </main>

      <Footer />
    </div>
  );
};

export default Index;
